import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {
    Button,
    FormControl,
    FormControlLabel,
    FormLabel,
    IconButton,
    LinearProgress,
    Radio,
    RadioGroup,
    Stack,
    Typography
} from '@mui/material';
import {Proposal, ProposalStatus} from "../../types/Proposal";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import {keepPreviousData, useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import {createProposal, loadProposal, updateProposal, updateProposalPublic} from "../../service/proposal.service";
import {ProposalResourceControl} from "./ProposalResourceControl";
import {TextInput} from "../form/TextInput";
import {enqueueSnackbar} from "notistack";
import {DefaultError} from "@tanstack/query-core";
import {ProposalStatusDropdown} from "../form/dropdown/ProposalStatusDropdown";
import {DatePicker} from "@mui/x-date-pickers";
import dayjs from "dayjs";
import {adaptData} from "../../utils/IngestorConverters";
import {useAuthStore} from "../../store/auth.store";
import {v4} from 'uuid';
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import {ProposalTypeDropdown} from "../form/dropdown/ProposalTypeDropdown";
import {useFormik} from "formik";
import * as Yup from "yup";
import {containMyPhone} from "../../utils/PhoneUtils";
import {Map} from "../map/Map";
import {loadAddressSuggestions, searchAddressFor} from "../../service/map.service";
import {AutoComplete} from "../form/AutoComplete";
import {TelegramButton} from "./messengers/TelegramButton";

function value100(def: number, value: string) {
    const result = parseInt(value);
    if (isNaN(result) || !isFinite(result)) {
        return def;
    }
    return result * 100;
}

function floatValue(value: number) {
    const out = Math.round(value / 100)
    return isNaN(out) ? '' : out;
}


export function ProposalFormToEdit({id, defaultData, publicEdit, goBack}: {
    publicEdit?: boolean,
    id?: string,
    defaultData?: Proposal,
    goBack: (() => void)
}) {
    const queryClient = useQueryClient()
    const {user, can, jwt, setShowRegister} = useAuthStore();
    const canManage = can('manage_proposals')
    const [addressVersion, setAddressVersion] = useState(0);
    const [initialAddress, setInitialAddress] = useState<string>();
    const [mapLookUpLoading, setMapLookUpLoading] = useState(false);
    const [addressManuallyTyped, setAddressManuallyTyped] = useState(false);
    const [addressHint, setAddressHint] = useState('');
    const [addressLookupValue, setAddressLookupValue] = useState('');

    const fullAccess = can('full_access')
    const isNew = id === 'new'
    const [submitHappen, setSubmitHappen] = useState(false);

    useEffect(() => {
        if (!jwt) {
            setShowRegister(true)
        }
    }, [jwt, setShowRegister]);

    const ProposalSchema = useMemo(() => {
        return Yup.object<Proposal>().shape({
            type: Yup.string().required("Type is required"),
            phone: Yup.string().required("Phone is required"),
            name: Yup.string().required("Name is required"),
            area: Yup.string().required("Area is required"),
            address: Yup.string().required("Address is required"),
            floor: Yup.string().required("Floor is required"),
            //floors: Yup.string().required("Password is required"),
            cadastralCode: id === 'new' ? Yup.string().required() : Yup.string().notRequired()
        });
    }, [id])



    const formik = useFormik<Proposal>({
        initialValues: defaultData || ({
            phone: id === 'new' ? user?.phone : undefined,
            name: id === 'new' ? user?.username : undefined,
        } as Proposal),
        validationSchema: ProposalSchema,
        onSubmit: (values) => {
            setSubmitHappen(true);
            updateMutation.mutate(values);
        },
    });

    const {setValues} = formik


    const addressLookup = useCallback(() => {
        if (!formik.values.address) {
            setMapLookUpLoading(false);
            return;
        }
        setMapLookUpLoading(true)
        searchAddressFor(formik.values.address).then(result => {
            if (result.data.lat !== undefined && result.data.lng !== undefined) {
                setValues({...formik.values, lat: result.data.lat, lng: result.data.lng});
                setAddressVersion(addressVersion + 1);
            }
        }).finally(() => {
            setMapLookUpLoading(false)
        })
    }, [formik.values.address, setValues/*, addressVersion*/]);

    useEffect(() => {
        setMapLookUpLoading(initialAddress !== formik.values.address)
        const timeout = setTimeout(() => {
            if (initialAddress === formik.values.address) {
                setMapLookUpLoading(false)
                return
            }
            addressLookup();
        }, 1500);
        return () => {
            setMapLookUpLoading(false);
            clearTimeout(timeout)
        }
    }, [initialAddress, formik.values.address, addressLookup]);

    const canEdit = canManage
        || id === 'new'
        || publicEdit
        || (user && user.id && formik.values.createdBy === user.id)
        || containMyPhone(user?.phone, formik.values.phone);

    useEffect(() => {
        if (!isNew || defaultData) {
            return;
        }
        formik.setValues({
            source: 'goodplacegeorgia',
            externalId: '',
            editId: v4(),
            createdAt: new Date().getTime(),
            proposalStatus: 'MANUAL',
            price: 0,
            area: 0,
            phone: user?.phone,
            name: user?.username
        } as Proposal, false)
        // eslint-disable-next-line
    }, [isNew, defaultData, user]);

    const {isLoading} = useQuery({
        queryKey: ['proposals', 'single', id],
        refetchOnWindowFocus: false,
        enabled: id !== 'new' && !defaultData,
        queryFn: () => loadProposal(id!)
            .then(response => {
                formik.setValues(response.data, true)
                setInitialAddress(response.data.address);
                return response.data;
            }).catch(error => {
                enqueueSnackbar({message: "Failed to load proposal: " + error.message, variant: 'error'})
            }),
        placeholderData: keepPreviousData
    });
    const {isLoading:isAddressOptionsLoading, data: addressOptionsData} = useQuery({
        queryKey: ['addressSuggestions', addressLookupValue],
        refetchOnWindowFocus: false,
        queryFn: () => loadAddressSuggestions(addressLookupValue!)
            .then(s => {
                const out = s.data;
                if(out[0] && out[0].indexOf(",") > -1) {
                    out.unshift(out[0].substring(0, out[0].indexOf(",")));
                }
                return out
            })
            .catch(error => {
                enqueueSnackbar({message: "Failed to load address suggestions: " + error.message, variant: 'error'})
                return []
            }),
        placeholderData: keepPreviousData
    });


    const updateMutation = useMutation<Proposal, DefaultError, Proposal>({
        mutationFn: (payload) => (id !== 'new'
                ? (publicEdit ? updateProposalPublic(payload) : updateProposal(payload))
                : createProposal(payload)
        ).then(r => {
            if (id === 'new') {
                enqueueSnackbar({variant: "success", message: "Created"})
            }
            return r.data
        }),
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey: ["proposals"]}).then(() => {
                goBack();
            }).catch((e) => {
                enqueueSnackbar({message: "Failed to invalidate proposals cache " + e.message, variant: 'error'})
            })
        },
        onError: (error) => {
            enqueueSnackbar({message: "Failed to persist proposal " + error.message, variant: 'error'})
        }
    });


    const handleCopy = async (editId: string) => {
        try {
            await navigator.clipboard.writeText(buildLink(editId));
            enqueueSnackbar({variant: "success", message: "Copied"})
        } catch (err) {
            console.error("Failed to copy text: ", err);
        }
    };

    function buildLink(editId: string) {
        return document.location.protocol + "//" + document.location.host + "/public-edit/" + editId;
    }

    const onLocationSelect = useCallback((position: { address: string }) => {
        if (!canEdit) {
            return;
        }
        const {address} = position;
        if (!address) {
            enqueueSnackbar({
                message: "Address not found. Please select a valid location, or enter address manually",
                variant: "error"
            });
        }
        console.log("address", address);
        formik.setFieldValue('address', address || "", true);
        setInitialAddress(address || "");
    }, [canEdit, formik]);


    return <form style={{maxWidth: 800, margin: '0 auto'}} onSubmit={async e => {
        e.preventDefault();
        const errors = await formik.validateForm();
        if (Object.keys(errors).length > 0) {
            enqueueSnackbar({message: "Validation fail, please check your data one more time ", variant: 'error'})
            console.log(errors)
        } else {
            formik.setSubmitting(true);
            formik.handleSubmit(e);
        }
    }}>

        {canEdit && <ControlPanel
            canManageProposals={canManage}
            proposal={formik.values}
            goBack={goBack}
            className={"mb-25"}
            preSubmit={() => setSubmitHappen(true)}/>}

        {(isLoading || formik.isSubmitting) && <LinearProgress/>}
        <Stack flexDirection={"column"} gap={"15px"}>
            {!isNew && fullAccess && <TextInput label="External ID"
                                                name="externalId"
                                                formik={formik}
                                                submitHappen={submitHappen}
                                                disabled/>}
            {!isNew && fullAccess && <TextInput label="Source"
                                                name="source"
                                                submitHappen={submitHappen}
                                                disabled
                                                formik={formik}/>}
            {!!formik.values.editId && <Stack>
                <TextInput label="Edit ID"
                           name="editId"
                           disabled
                           submitHappen={submitHappen}
                           endAdorment={<IconButton
                               onClick={() => handleCopy(formik.values.editId)}>
                               <ContentCopyIcon color={"success"}/>
                           </IconButton>}
                           value={buildLink(formik.values.editId)}/>
                {!jwt && <Typography color={"error"}>
                    You don’t currently have an account in our system.
                    Please save this link so you can manage your advertisement in the future,
                    as it will not be shown again after you submit the form.
                </Typography>}
            </Stack>}

            <ProposalStatusDropdown
                disabled={!canEdit}
                label={"Proposal Status"}
                allowEmpty
                submitHappen={submitHappen}
                onChange={e => {
                    formik.setFieldValue('proposalStatus', (e[e.length - 1] || 'MANUAL') as ProposalStatus, false);
                }}
                value={formik.values.proposalStatus ? [formik.values.proposalStatus] : []}
            />

            {formik.values.proposalStatus === 'REJECTED' && canEdit &&
                <TextInput label="Rejected Reason" name="rejectedReason" submitHappen={submitHappen} formik={formik}/>}

            {!isNew && <DatePicker label="Created At"
                                   disabled
                                   name="createdAt"
                                   value={dayjs(formik.values.createdAt)}/>}
            {!isNew && canEdit && formik.values.createdAtExternal && <TextInput
                label="Created At External"
                disabled
                submitHappen={submitHappen}
                name="createdAtExternal"
                value={formik.values.createdAtExternal}
            />}


            {fullAccess && !isNew && <Stack flexDirection={"row"} alignItems={"center"}>
                <TextInput sx={{flexGrow: 1}}
                           submitHappen={submitHappen}
                           label="Link"
                           name="link"
                           disabled
                           value={formik.values.link}
                           onChange={(e) => {
                               formik.setFieldValue('link', e.target.value, true);
                           }}/>
                {formik.values.link &&
                    <IconButton href={adaptData(formik.values.source, 'link', formik.values.link)} target={"_blank"}>
                        <OpenInNewIcon color={"info"}/>
                    </IconButton>}
                {!formik.values.link && <IconButton>
                    <OpenInNewIcon color={"disabled"}/>
                </IconButton>}
            </Stack>}

            <ProposalTypeDropdown value={adaptData(formik.values.source, 'type', formik.values.type)}
                                  formik={formik}
                                  disabled={!canEdit}
                                  submitHappen={submitHappen}
                                  label={'Type'}
                                  name={'type'}/>

            <TextInput label="Description"
                       name="description"
                       disabled={!canEdit}
                       submitHappen={submitHappen}
                       formik={formik}
                       multiline
                       rows={4}/>

            {canManage && <TextInput label="Admin Notes"
                                     name="adminNotes"
                                     disabled={!canEdit}
                                     formik={formik}
                                     submitHappen={submitHappen}
                                     multiline
                                     rows={4}/>}


            <TextInput label="Phone"
                       name="phone"
                       disabled={!canEdit}
                       submitHappen={submitHappen}
                       formik={formik}/>


            <TextInput label="Contact Name"
                       name="name"
                       disabled={!canEdit}
                       submitHappen={submitHappen}
                       formik={formik}/>


            <TextInput label="Price"
                       name="price"
                       disabled={!canEdit}
                       formik={formik}
                       submitHappen={submitHappen}
                       value={floatValue(formik.values.price)}
                       onChange={(e) => {
                           formik.setFieldValue('price', value100(formik.values.price, e.target.value), true)
                       }}/>


            <TextInput label="Area"
                       name="area"
                       disabled={!canEdit}
                       formik={formik}
                       submitHappen={submitHappen}
                       value={floatValue(formik.values.area)}
                       onChange={(e) => {
                           formik.setFieldValue('area', value100(formik.values.area, e.target.value), true)
                       }}/>


            <AutoComplete
                label="Address"
                options={(addressOptionsData || []).map(s => ({value: s, label: s}))}
                name="address"
                isLoading={isAddressOptionsLoading}
                disabled={!canEdit}
                valueRender={v => v || ""}
                textField={{
                    onChange: e => {
                        console.log("textfield onChange");
                        const address = (e || "").replace(/[^a-zA-Z0-9:,\s-]/g, '');
                        if (e !== null && address !== e) {
                            enqueueSnackbar("Only english characters and digits allowed")
                        }
                        setAddressLookupValue(address);
                        setAddressManuallyTyped(true)
                        formik.setFieldValue('address', '');
                    },
                    value: addressLookupValue
                }}
                onChange={(a, opt, setIsOpen, reason) => {
                    console.log("autocomplete onChange");
                    if(a && a?.indexOf(",") === -1) {
                        setAddressLookupValue(a);
                        setIsOpen(true);
                    } else {
                        formik.setFieldValue('address', a, true);
                        if(reason !== 'textfield-on-change') {
                            setIsOpen(false);
                        }
                    }
                }}
                //onBlur={() => addressLookup()}
                submitHappen={submitHappen}
                formik={formik}/>
            {addressHint && addressHint !== formik.values.address && <Button onClick={() => {
                formik.setFieldValue('address', addressHint);
                setInitialAddress(addressHint);
            }}>Automatically detected address: {addressHint}</Button>}
            <Map
                loading={mapLookUpLoading}
                version={addressVersion}
                autoSelectLocation
                setMarker={(m) => {
                    formik.setValues({...formik.values, lat: m.lat, lng: m.lng})
                    if (!addressManuallyTyped) {
                        setAddressVersion(addressVersion + 1)
                    }
                }}
                marker={{lat: formik.values.lat, lng: formik.values.lng}}
                onLocationSelect={location => {
                    if (addressManuallyTyped) {
                        setAddressHint(location.address)
                    } else {
                        onLocationSelect(location)
                    }
                }}
            />

        <TextInput label="Floor"
                       name="floor"
                       type={'number'}
                       disabled={!canEdit}
                       submitHappen={submitHappen}
                       formik={formik}/>

            <TextInput label="Floors Total"
                       name="floorMax"
                       type={'number'}
                       disabled={!canEdit}
                       submitHappen={submitHappen}
                       formik={formik}/>


            {/*<TextInput label="Rooms" name="rooms"
                                  disabled={!canEdit} value={formData.rooms} onChange={(e) => {
                            setFormData({ ...formData, rooms: e.target.value })
                        }}/>*/}


        <FormControl>
                <FormLabel id="property-condition-label">
                    Property Condition
                </FormLabel>

                <RadioGroup
                    aria-labelledby="property-condition-label"
                    name="property-condition"
                    value={formik.values.renovationStatus || null}
                    onChange={e => formik.setFieldValue('renovationStatus', e.currentTarget.value)}
                >
                    <FormControlLabel
                        value="RENOVATED"
                        control={<Radio/>}
                        label="Renovated"
                    />
                    <FormControlLabel
                        value="RENOVATED_FURNISHED"
                        control={<Radio/>}
                        label="Renovated & Furnished"
                    />
                    <FormControlLabel
                        value="NEEDS_RENOVATION"
                        control={<Radio/>}
                        label="Needs Renovation"
                    />
                </RadioGroup>
            </FormControl>

            <TextInput label="Windows" name="windows"
                       disabled={!canEdit}
                       submitHappen={submitHappen}
                       formik={formik}/>


            <TextInput label="View" name="view" disabled={!canEdit} submitHappen={submitHappen} formik={formik}/>


            <TextInput label="Heating"
                       name="heating"
                       disabled={!canEdit}
                       submitHappen={submitHappen}
                       formik={formik}/>

            <TextInput label="Cadastral Code"
                       name="cadastralCode"
                       disabled={!canEdit}
                       submitHappen={submitHappen}
                       formik={formik}/>

            <Stack flexDirection={"row"}>
                <TextInput label="WhatsApp"
                           disabled={!canEdit}
                           submitHappen={submitHappen}
                           name="whatsapp"
                           formik={formik}/>
                {formik.values.whatsapp &&
                    <IconButton href={`https://wa.me/${formik.values.whatsapp}`} target="_blank"
                                rel="noopener noreferrer">
                        <WhatsAppIcon color={"success"}/>
                    </IconButton>}
                {!formik.values.whatsapp &&
                    <IconButton>
                        <WhatsAppIcon/>
                    </IconButton>}
            </Stack>

            <ProposalResourceControl canEdit={canEdit}
                                     resources={formik.values.resources || []}
                                     setResources={(resources) => {
                                         formik.setValues({...formik.values, resources: resources})
                                     }}/>
        </Stack>

        {canEdit && <ControlPanel
            canManageProposals={canManage}
            proposal={formik.values}
            goBack={goBack}
            preSubmit={() => setSubmitHappen(true)}/>}
    </form>
}


function ControlPanel({goBack, proposal, canManageProposals, className, preSubmit}: {
    preSubmit: () => void,
    canManageProposals: boolean,
    className?: string,
    proposal: Proposal,
    goBack: () => void
}) {
    return <Stack flexDirection={"row"} gap={"20px"} className={className}>
        <Button color="inherit" variant={"contained"} onClick={() => {
            goBack()
        }}>Back</Button>
        <Button type="submit" variant="contained" color="primary" onClick={preSubmit}>Submit</Button>
        {canManageProposals && proposal.id && <TelegramButton
            callback={() => undefined}
            proposal={proposal}
            sx={{marginLeft: 'auto'}}/>
        }
    </Stack>
}